/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useMemo } from 'react';
import { Modal } from '../../../../UiKit';
import { ToolTip } from '../../../ComponentsV1/ToolTip/Tooltip';
import { getOrgIdFromUrl } from '../../../../utils/UtilFunctions';
import { OFSE_SENDER_FIELDS, OFSE_RECEIVER_FIELDS } from './constants';

export const InfoStrip = ({
  title,
  value,
  isId,
  idUrl,
  tooltipContent = '',
}) => {
  return (
    <div className='tw-w-full tw-flex tw-justify-between tw-items-center'>
      <span className='tw-text-lg tw-font-medium tw-text-grey-800'>
        {title}
      </span>
      <ToolTip
        disabled={!(tooltipContent && idUrl)}
        placement='bottom'
        content={tooltipContent}
      >
        <span
          onClick={() => {
            if (idUrl) {
              window.open(idUrl, '_blank', 'noopener,noreferrer');
            }
          }}
          className={`tw-font-medium ${
            isId
              ? `tw-p-1 tw-border ${
                  idUrl
                    ? 'tw-underline tw-cursor-pointer tw-underline-offset-2'
                    : ''
                } tw-border-solid tw-border-grey-200 tw-shadow-sm tw-bg-grey-100 tw-rounded-lg tw-text-grey-700 tw-text-[1.4rem] tw-px-3`
              : 'tw-text-sm tw-text-grey-700'
          }`}
          data-dd-privacy='mask'
        >
          {value || '-'}
        </span>
      </ToolTip>
    </div>
  );
};

const normalizeDetails = (details) => {
  if (!details) return [];
  if (Array.isArray(details)) return details;
  return [details];
};

const normalizeSenderDetails = (senderDetails) =>
  OFSE_SENDER_FIELDS.map((key) => {
    const found = senderDetails.find((info) => info?.key === key);
    return found || { key, value: ['NA'] };
  });

const normalizeReceiverDetails = (receiverDetails) =>
  OFSE_RECEIVER_FIELDS.map((key) => {
    const found = receiverDetails.find((info) => info?.key === key);
    return found || { key, value: ['NA'] };
  });

const getInfoStripProps = (info, orgId) => {
  const displayValue =
    info.value?.filter((v) => v != null && v !== 'null').join(', ') || 'NA';
  const isId = info.key?.toLowerCase().includes('id');
  const idUrl =
    displayValue && isId && displayValue !== 'NA'
      ? `https://lifeline.blinkit.com/lifeline?type=user_id&value=${info.value}&org_id=${orgId}`
      : '';
  return { displayValue, isId, idUrl };
};

const DetailsComponent = ({ tagInfo }) => {
  const orgId = getOrgIdFromUrl();

  const senderDetails = useMemo(() => {
    const [raw] = tagInfo?.data ?? [[]];
    return normalizeSenderDetails(normalizeDetails(raw));
  }, [tagInfo?.data]);

  const receiverDetailsArray = useMemo(() => {
    const [, ...rest] = tagInfo?.data ?? [[], []];
    return rest.map((rd) => normalizeReceiverDetails(normalizeDetails(rd)));
  }, [tagInfo?.data]);

  if (tagInfo.title === 'OFSE Details') {
    return (
      <div className='tw-flex tw-flex-col tw-gap-6'>
        <div>
          <h3 className='tw-font-medium tw-text-grey-900 tw-mb-2'>
            Sender Details
          </h3>
          <div className='tw-border tw-border-solid tw-border-grey-200 tw-rounded-lg tw-bg-white tw-flex tw-flex-col tw-gap-4 tw-p-4'>
            {senderDetails.map((info) => {
              if (!info) return null;
              const { displayValue, isId, idUrl } = getInfoStripProps(
                info,
                orgId,
              );
              return (
                <InfoStrip
                  key={info.key}
                  title={info.key}
                  value={displayValue}
                  isId={isId}
                  idUrl={idUrl}
                  tooltipContent={'View sender profile'}
                />
              );
            })}
          </div>
        </div>
        {receiverDetailsArray.map((receiverDetails, receiverIndex) => (
          <div key={`receiver-${receiverIndex}`}>
            <h3 className='tw-font-medium tw-text-grey-900 tw-mb-2'>
              {receiverDetailsArray.length === 1
                ? 'Receiver Details'
                : `Receiver ${receiverIndex + 1} Details`}
            </h3>
            <div className='tw-border tw-border-solid tw-border-grey-200 tw-rounded-lg tw-bg-white tw-flex tw-flex-col tw-gap-4 tw-p-4'>
              {receiverDetails.map((info, receiverIndex) => {
                if (!info) return null;
                const { displayValue, isId, idUrl } = getInfoStripProps(
                  info,
                  orgId,
                );
                return (
                  <InfoStrip
                    key={`${info.key}-${receiverIndex}`}
                    title={info.key}
                    value={displayValue}
                    isId={isId}
                    idUrl={idUrl}
                    tooltipContent={'View receiver profile'}
                  />
                );
              })}
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className='tw-w-full tw-flex tw-flex-col tw-gap-2'>
      {tagInfo?.data.map((elements, index) => (
        <div key={index}>
          {elements.map((info, ind) => (
            <div className='tw-flex tw-w-full tw-gap-2' key={`${ind}_info_key`}>
              <div className='tw-font-semibold tw-text-300 tw-text-grey-900 tw-w-[30%]'>{`${info.key} : `}</div>
              <div className='tw-flex tw-flex-col tw-gap-2 tw-w-[70%] tw-text-grey-800'>
                {info.value.map((value, index) => (
                  <div className='tw-flex' key={`${index}_${ind}_info_value`}>
                    <span className='tw-flex tw-text-justify tw-text-300 tw-font-medium'>{` ${
                      info.value.length > 1 ? `${index + 1} - ` : ``
                    } ${value}`}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
          {index + 1 < tagInfo.data.length && (
            <div className='tw-w-full tw-border-solid tw-border-[0.1rem] tw-border-bottom-0 tw-border-x-0 tw-border-grey-200 tw-rounded-lg tw-my-2'></div>
          )}
        </div>
      ))}
    </div>
  );
};

export const OrderTagDetailsModal = ({ tagInfo, handleClose }) => {
  if (!tagInfo) return null;
  return (
    <Modal
      isVisible
      type={
        window.dashboard.mode == 'smartplug'
          ? 'large'
          : tagInfo.title === 'OFSE Details'
          ? 'large'
          : 'medium'
      }
      title={tagInfo?.title}
      maxHeight='60vh'
      height=''
      handleClose={handleClose}
      component={<DetailsComponent tagInfo={tagInfo} />}
    />
  );
};
